import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { PropsWithChildren, useEffect, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
  applyThemeToBody,
  getStoredTheme,
  resolveTheme,
  setStoredTheme,
} from "@/lib/themePreference";
import { getTenantIdFromToken } from "@/utils/auth";
import { useCachedAccessToken } from "@/hooks/useCachedAccessToken";
import { ST_DOCUMENTS_BASE_URL } from "@/services/appServices";
import { logger } from "@/lib/logger";
import { Theme, ThemeContext } from "./ThemeContext";

const ThemeProvider: React.FC<PropsWithChildren> = ({ children }) => {
  /**** Hooks ****/
  const queryClient = useQueryClient();

  const getCachedToken = useCachedAccessToken();
  // Both effects below read the tenant out of the access token. On the sign-in
  // screen there is none, and asking anyway used to end as an interactive login
  // (LAR-437) — see `useCachedAccessToken`.
  const { isAuthenticated } = useAuth0();

  // Add debounce mechanism
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Global flag to prevent multiple simultaneous refreshes
  const isRefreshingRef = useRef(false);

  const getTenantStylesheet = async () => {
    // Get tenant ID
    const tenantId = await getTenantIdFromToken(getCachedToken);

    // Get stylesheet
    const storedColorScheme = sessionStorage.getItem(`style-${tenantId}`);
    let cssText = storedColorScheme;

    if (!storedColorScheme) {
      // Fetch from blob container
      const timestamp = new Date().getTime(); // Current timestamp for cache-busting

      const stylesheetUrl = `${ST_DOCUMENTS_BASE_URL}/${tenantId}-assets/stylesheet.css?cb=${timestamp}`;

      const response = await fetch(stylesheetUrl, { cache: "no-store" });
      if (!response.ok) {
        return;
      }
      cssText = await response.text();

      // Add !important to each variable for priority
      cssText = cssText.replace(/(--[a-zA-Z0-9-]+: [^;]+);/g, "$1 !important;");
    }

    // Set stylesheet
    if (cssText) {
      injectStyles(cssText);
      sessionStorage.setItem(`style-${tenantId}`, cssText);
    }
  };

  const refreshTenantStylesheet = async () => {
    // Prevent multiple simultaneous refreshes
    if (isRefreshingRef.current) {
      return;
    }

    // Clear any existing timeout
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }

    // Debounce the refresh to prevent multiple rapid calls
    refreshTimeoutRef.current = setTimeout(async () => {
      if (isRefreshingRef.current) return;

      isRefreshingRef.current = true;

      try {
        // Clear existing stylesheet from session storage to force refresh
        const tenantId = await getTenantIdFromToken(getCachedToken);

        // Clear both old and new tenant stylesheets
        sessionStorage.removeItem(`style-${tenantId}`);

        // Also clear any existing stylesheets from DOM
        const existingStyles = document.querySelectorAll(
          "style[data-tenant-stylesheet]"
        );
        existingStyles.forEach((style) => style.remove());

        // Wait a bit to ensure DOM cleanup is complete
        await new Promise((resolve) => setTimeout(resolve, 100));

        await getTenantStylesheet();
      } catch (error) {
        logger.warn("Failed to refresh tenant stylesheet", {
          operation: "refreshTenantStylesheet",
          error,
        });
      } finally {
        isRefreshingRef.current = false;
      }
    }, 500); // 500ms debounce
  };

  useEffect(() => {
    if (!isAuthenticated) return;
    getTenantStylesheet();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Initialize tenant ID on first load
  useEffect(() => {
    const initializeTenantId = async () => {
      try {
        const currentTenantId = await getTenantIdFromToken(getCachedToken);
        if (currentTenantId) {
          sessionStorage.setItem("current-tenant-id", currentTenantId);
        }
      } catch (error) {
        logger.warn("Failed to read tenant id from the access token", {
          operation: "initializeTenantId",
          error,
        });
      }
    };

    if (isAuthenticated) initializeTenantId();
  }, [getCachedToken, isAuthenticated]);

  const injectStyles = (css: string) => {
    // Remove any existing tenant stylesheets
    const existingStyles = document.querySelectorAll(
      "style[data-tenant-stylesheet]"
    );
    existingStyles.forEach((style) => style.remove());

    const style = document.createElement("style");
    style.setAttribute("data-tenant-stylesheet", "true");
    style.textContent = css;
    document.head.appendChild(style);

    // Clean up on component unmount
    return () => {
      document.head.removeChild(style);
    };
  };
  // Get Query
  const useQueryTheme = () =>
    useQuery({
      queryKey: ["theme"],
      queryFn: async () => {
        const storedTheme = getStoredTheme();
        const cachedTheme = queryClient.getQueryData<Theme>(["theme"]);
        return resolveTheme(storedTheme, cachedTheme);
      },
    });

  /**** Queries ****/
  const fetchedTheme = useQueryTheme();

  //Fetch Theme
  useEffect(() => {
    if (fetchedTheme.isFetched && fetchedTheme.data) {
      toggleTheme(fetchedTheme.data as Theme);
    }
  }, [fetchedTheme]);

  //Toggle Theme - This handles the actual DOM manipulation with transitions disabled
  const toggleTheme = (theme: Theme) => {
    // Disable transitions
    document.documentElement.classList.add("disable-transitions");

    requestAnimationFrame(() => {
      applyThemeToBody(theme);

      // Re-enable transitions after the change is applied
      requestAnimationFrame(() => {
        document.documentElement.classList.remove("disable-transitions");
      });
    });
  };

  //Set Mutation
  const setTheme = useMutation({
    mutationKey: ["theme", "set"],
    mutationFn: async (theme: Theme) => {
      queryClient.setQueryData(["theme"], theme);
      setStoredTheme(theme);
      toggleTheme(theme);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["theme"],
        exact: true,
      });
    },
  });

  return (
    <ThemeContext.Provider
      value={{
        useQueryTheme,
        setTheme,
        refreshTenantStylesheet,
      }}
    >
      <div>{children}</div>
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;
